import { MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useIsAdministrator } from '../auth/useCallerContext';
import { useSelectedTenant } from '../tenant/tenantSelection';

/**
 * The global tenant switcher. A single-tenant caller has no choice to make, but still sees which
 * tenant they are working in — the same field, read-only. An administrator always gets the
 * dropdown, even with one tenant registered: their working context is a decision, never a
 * default. "Not selected" is a choice too: it drops the selection — for an administrator the
 * tenant-dependent sections then wait, disabled, for the next pick; for a multi-tenant caller it
 * brings the full-page chooser back.
 */
export function TenantMenu() {
  const { t } = useTranslation();
  const isAdministrator = useIsAdministrator();
  const { tenants, selectedTenantGid, selectTenant, clearSelection } = useSelectedTenant();

  if (!isAdministrator && tenants.length <= 1) {
    // With exactly one tenant it is the active one by construction — auto-selected here, and
    // auto-resolved the same way by the server even without the header.
    const only = tenants[0];
    if (only === undefined) return null;

    return (
      <TextField
        size="small"
        label={t('tenants.label')}
        value={only.name}
        slotProps={{ input: { readOnly: true } }}
        sx={{ minWidth: 200 }}
      />
    );
  }

  return (
    <TextField
      select
      size="small"
      label={t('tenants.label')}
      value={selectedTenantGid ?? ''}
      onChange={(event) => {
        if (event.target.value === '') clearSelection();
        else selectTenant(event.target.value);
      }}
      sx={{ minWidth: 200 }}
    >
      <MenuItem value="">
        <em>{t('tenants.selectorNone')}</em>
      </MenuItem>
      {tenants.map((tenant) => (
        <MenuItem key={tenant.tenantGid} value={tenant.tenantGid}>
          {tenant.name}
        </MenuItem>
      ))}
    </TextField>
  );
}
